import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  KeyRound, Plus, RotateCw, Power, Trash2, Link2, Unplug, ShieldCheck,
  Loader2, AlertTriangle, CreditCard, Search, CheckCircle2, Save, Pencil, X, BarChart3,
} from 'lucide-react';
import { api } from '@/lib/api';
import { luxe } from '@/lib/luxe';
import { GlassCard, LuxeButton, Input, Spinner, Empty } from '@/components/bits';

const ACCENT = luxe.blue;

const Badge = ({ text, tone = luxe.muted }) => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
    style={{ background: luxe.surface2, color: tone, border: `1px solid ${luxe.border}` }}>{text}</span>
);

const Row = ({ children, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 p-3 rounded-lg mb-2"
    style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>{children}</div>
);

// ---------------- Secrets ----------------
const SECRET_PRESETS = [
  { id: 'emergent-llm', label: 'EMERGENT_LLM_KEY', group: 'AI', provider: 'emergent', purpose: 'llm', hint: 'Enter Emergent LLM key', vault: true },
  { id: 'openai', label: 'OPENAI_API_KEY', group: 'AI', provider: 'openai', purpose: 'text generation', hint: 'sk-…', vault: true },
  { id: 'admin-email', label: 'ADMIN_EMAIL', group: 'Admin / security', provider: 'deployment-admin-email', purpose: 'ADMIN_EMAIL deployment setting', hint: 'admin@example.com', vault: false },
  { id: 'admin-password', label: 'ADMIN_PASSWORD', group: 'Admin / security', provider: 'deployment-admin-password', purpose: 'ADMIN_PASSWORD deployment setting', hint: 'Set in deployment environment', vault: false },
  { id: 'jwt-secret', label: 'JWT_SECRET', group: 'Admin / security', provider: 'deployment-jwt-secret', purpose: 'JWT_SECRET deployment setting', hint: 'Set in deployment environment', vault: false },
  { id: 'jwt-expires', label: 'JWT_EXPIRES_HOURS', group: 'Admin / security', provider: 'deployment-jwt-expires-hours', purpose: 'JWT_EXPIRES_HOURS deployment setting', hint: 'e.g. 24', vault: false },
  { id: 'social-enc', label: 'SOCIAL_ENC_KEY', group: 'Admin / security', provider: 'deployment-social-enc-key', purpose: 'SOCIAL_ENC_KEY deployment setting', hint: 'Do not rotate without migration', vault: false },
  { id: 'frontend-url', label: 'FRONTEND_URL', group: 'Website', provider: 'deployment-frontend-url', purpose: 'FRONTEND_URL build/deployment setting', hint: 'https://myraai.in', vault: false },
  { id: 'public-base-url', label: 'PUBLIC_BASE_URL', group: 'Website', provider: 'deployment-public-base-url', purpose: 'PUBLIC_BASE_URL deployment setting', hint: 'https://myraai.in', vault: false },
  { id: 'react-backend-url', label: 'REACT_APP_BACKEND_URL', group: 'Website', provider: 'deployment-react-app-backend-url', purpose: 'REACT_APP_BACKEND_URL build setting', hint: 'https://myraai.in/api', vault: false },
  { id: 'integration-proxy', label: 'INTEGRATION_PROXY_URL', group: 'Integrations', provider: 'deployment-integration-proxy-url', purpose: 'INTEGRATION_PROXY_URL deployment setting', hint: 'Proxy URL', vault: false },
  { id: 'linkedin-id', label: 'LINKEDIN_OAUTH_CLIENT_ID', group: 'Integrations', provider: 'deployment-linkedin-client-id', purpose: 'LinkedIn OAuth client ID', hint: 'LinkedIn client ID', vault: false },
  { id: 'linkedin-secret', label: 'LINKEDIN_OAUTH_CLIENT_SECRET', group: 'Integrations', provider: 'linkedin', purpose: 'oauth client secret', hint: 'LinkedIn client secret', vault: true },
  { id: 'linkedin-version', label: 'LINKEDIN_VERSION', group: 'Integrations', provider: 'deployment-linkedin-version', purpose: 'LinkedIn API version', hint: 'API version', vault: false },
  { id: 'telegram-token', label: 'TELEGRAM_BOT_TOKEN', group: 'Integrations', provider: 'telegram', purpose: 'bot token', hint: '123456:ABC…', vault: true },
  { id: 'telegram-username', label: 'TELEGRAM_BOT_USERNAME', group: 'Integrations', provider: 'deployment-telegram-username', purpose: 'Telegram bot username', hint: '@your_bot', vault: false },
  { id: 'cors-origins', label: 'CORS_ORIGINS', group: 'System / database', provider: 'deployment-cors-origins', purpose: 'CORS_ORIGINS deployment setting', hint: 'https://myraai.in', vault: false },
  { id: 'db-name', label: 'DB_NAME', group: 'System / database', provider: 'deployment-db-name', purpose: 'DB_NAME deployment setting', hint: 'Database name', vault: false },
  { id: 'health-check', label: 'ENABLE_HEALTH_CHECK', group: 'System / database', provider: 'deployment-enable-health-check', purpose: 'ENABLE_HEALTH_CHECK deployment setting', hint: 'true or false', vault: false },
  { id: 'mongo-url', label: 'MONGO_URL', group: 'System / database', provider: 'deployment-mongo-url', purpose: 'MONGO_URL deployment setting', hint: 'Set in deployment environment', vault: false },
  { id: 'wds-port', label: 'WDS_SOCKET_PORT', group: 'System / database', provider: 'deployment-wds-socket-port', purpose: 'WDS_SOCKET_PORT build setting', hint: 'Port number', vault: false },
  { id: 'custom', label: 'Other Secret', group: 'Other', provider: '', purpose: '', hint: 'Paste secret value', vault: true },
];

export function SecretsTab() {
  const [items, setItems] = useState(null);
  const [denied, setDenied] = useState(false);
  const [selected, setSelected] = useState('openai');
  const [form, setForm] = useState({ provider: 'openai', purpose: 'text generation', value: '', scope: 'PLATFORM' });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const preset = SECRET_PRESETS.find((p) => p.id === selected) || SECRET_PRESETS[0];

  const choosePreset = (id) => {
    setSelected(id);
    const p = SECRET_PRESETS.find((x) => x.id === id) || SECRET_PRESETS[0];
    setForm((old) => ({ ...old, provider: p.provider, purpose: p.purpose, value: '' }));
  };
  const load = () => api('/admin/manage/secrets')
    .then((r) => { setItems(r.secrets || []); setDenied(false); })
    .catch((e) => { if (e.status === 403) { setDenied(true); setItems([]); } else toast.error(e.message); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!preset.vault) return toast.error('This is a deployment/build setting, not a vault credential. It must be changed in deployment configuration and may require a restart or frontend rebuild.');
    if (!form.provider.trim() || !form.purpose.trim() || !form.value.trim()) return toast.error('Please select a secret type and enter its value');
    setBusy(true);
    try {
      await api('/admin/manage/secrets', { method: 'POST', body: form });
      toast.success('Secret saved securely in the vault');
      setForm((old) => ({ ...old, value: '' }));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const rotate = async (s) => {
    const v = window.prompt(`Enter the replacement value for ${s.provider} — ${s.purpose}. It will be encrypted and never shown again.`);
    if (!v) return;
    try { await api(`/admin/manage/secrets/${s.id}/rotate`, { method: 'POST', body: { value: v } }); toast.success('Secret updated'); load(); } catch (e) { toast.error(e.message); }
  };
  const saveEditedSecret = async (s) => {
    if (!editValue.trim()) return toast.error('Enter the replacement secret first');
    setSavingEdit(true);
    try {
      await api(`/admin/manage/secrets/${s.id}/rotate`, {
        method: 'POST', body: { value: editValue.trim() }
      });
      toast.success('Secret updated securely');
      setEditingId(null);
      setEditValue('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not save secret');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggle = async (s) => {
    const next = s.status === 'active' ? 'disabled' : 'active';
    try { await api(`/admin/manage/secrets/${s.id}/status`, { method: 'POST', body: { status: next } }); load(); } catch (e) { toast.error(e.message); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete secret ${s.provider} — ${s.purpose}?`)) return;
    try { await api(`/admin/manage/secrets/${s.id}`, { method: 'DELETE' }); toast.success('Secret deleted'); load(); } catch (e) { toast.error(e.message); }
  };

  if (denied) return <Empty icon={ShieldCheck} title="Admin only" desc="Secret management requires a studio_admin account." />;

  return (
    <div data-testid="manage-secrets" className="space-y-4">
      <div className="rounded-2xl p-5 sm:p-6" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)', border: '1px solid #86efac', color: '#14532d' }}>
        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={21} /><h2 className="text-lg font-extrabold">Secrets Vault</h2></div>
        <p className="text-sm mb-4" style={{ color: '#166534' }}>Choose a service, enter its credential, then press Save. Secret values are encrypted before storage.</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {SECRET_PRESETS.map((p) => (
            <button key={p.id} type="button" onClick={() => choosePreset(p.id)} data-testid={`secret-preset-${p.id}`}
              className="text-left p-3 rounded-xl transition-colors"
              style={{ background: selected === p.id ? '#bbf7d0' : '#ffffff', border: `1px solid ${selected === p.id ? '#16a34a' : '#bbf7d0'}`, color: '#14532d' }}>
              <span className="block text-sm font-bold">{p.label}</span>
              <span className="block text-xs mt-1" style={{ color: '#15803d' }}>{selected === p.id ? 'Selected' : p.group}{p.vault ? ' · encrypted vault' : ' · deployment setting'}</span>
            </button>
          ))}
        </div>
        <label className="block text-sm font-bold mb-1" htmlFor="vault-secret-value">{preset.label}</label>
        <input id="vault-secret-value" data-testid="secret-value" type="password" autoComplete="new-password"
          value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
          placeholder={preset.hint} className="w-full rounded-xl px-3 py-3 text-sm outline-none mb-3"
          style={{ background: '#fff', color: '#14532d', border: '1px solid #86efac' }} />
        {selected === 'custom' && <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Provider name" data-testid="secret-provider" className="rounded-lg px-3 py-2 text-sm" style={{ background: '#fff', color: '#14532d', border: '1px solid #86efac' }} />
          <input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Purpose" data-testid="secret-purpose" className="rounded-lg px-3 py-2 text-sm" style={{ background: '#fff', color: '#14532d', border: '1px solid #86efac' }} />
        </div>}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={create} disabled={busy} data-testid="secret-create-btn" className="rounded-xl px-5 py-2.5 text-sm font-extrabold disabled:opacity-60" style={{ background: '#15803d', color: '#fff' }}>
            {busy ? <Loader2 size={15} className="inline mr-2 animate-spin" /> : <Plus size={15} className="inline mr-2" />}Save Secret
          </button>
          <span className="text-xs flex items-center gap-1" style={{ color: '#166534' }}><ShieldCheck size={13} /> Encrypted at rest · value never displayed</span>
        </div>
        <p className="text-xs mt-3" style={{ color: '#166534' }}>Credentials marked “encrypted vault” are saved here. Database, JWT, URL, CORS and build-time settings are listed for visibility but cannot safely be changed by storing them as ordinary secrets; use deployment configuration for those. Existing encryption keys are not rotated by this screen.</p>
      </div>

      <div>
        <div className="text-sm font-bold mb-2" style={{ color: luxe.text }}>Saved credentials</div>
        {items === null ? <div className="flex gap-2" style={{ color: luxe.muted }}><Spinner /> Loading…</div>
          : items.length === 0 ? <Empty icon={KeyRound} title="No secrets saved yet" desc="Choose a service above to add your first credential." />
            : items.map((s) => (
              <Row key={s.id} testid={`secret-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-bold truncate" style={{ color: luxe.text }}>{s.provider} <span style={{ color: luxe.muted }}>· {s.purpose}</span></div>
                  <div className="text-[11px]" style={{ color: luxe.muted }}>Scope {s.scope} · updated {(s.updated_at || s.created_at || '').slice(0, 16)}</div>
                  {editingId === s.id && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <input
                        type="password"
                        autoComplete="new-password"
                        aria-label={`Replacement secret for ${s.provider}`}
                        placeholder="Enter new secret value"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="min-w-[220px] flex-1 rounded-xl px-3 py-2 text-sm"
                        style={{ background: '#FFFFFF', color: '#4B1727', border: '1px solid #16BDBD' }}
                      />
                      <button
                        onClick={() => saveEditedSecret(s)}
                        disabled={savingEdit}
                        data-testid={`secret-save-${s.id}`}
                        className="rounded-xl px-4 py-2 text-sm font-extrabold disabled:opacity-60"
                        style={{ background: 'linear-gradient(180deg,#22CFC8,#087F83)', color: '#FFFFFF', boxShadow: '0 4px 0 #075F63, 0 7px 14px rgba(8,127,131,.22)' }}
                      >
                        {savingEdit ? <Loader2 size={14} className="inline mr-1 animate-spin" /> : <Save size={14} className="inline mr-1" />}
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditValue(''); }}
                        className="rounded-xl px-3 py-2 text-sm font-bold"
                        style={{ background: '#FFF4B8', color: '#4B1727', border: '1px solid #E7DCA8' }}
                      >
                        <X size={14} className="inline mr-1" />Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge text={s.status === 'active' ? 'Saved' : 'Disabled'} tone={s.status === 'active' ? luxe.success : luxe.muted} />
                  {editingId !== s.id && (
                    <button
                      title="Edit secret"
                      onClick={() => { setEditingId(s.id); setEditValue(''); }}
                      data-testid={`secret-edit-${s.id}`}
                      className="rounded-xl px-3 py-2 text-sm font-extrabold"
                      style={{ background: 'linear-gradient(180deg,#FFFCE5,#FFE65A)', color: '#4B1727', border: '1px solid #FFD900', boxShadow: '0 4px 0 #C5A900, 0 7px 14px rgba(197,169,0,.20)' }}
                    >
                      <Pencil size={14} className="inline mr-1" />Edit
                    </button>
                  )}
                  <button title="Enable/Disable" onClick={() => toggle(s)} data-testid={`secret-toggle-${s.id}`} className="p-2 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}><Power size={14} /></button>
                  <button title="Delete" onClick={() => del(s)} data-testid={`secret-delete-${s.id}`} className="p-2 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: '#dc2626' }}><Trash2 size={14} /></button>
                </div>
              </Row>
            ))}
      </div>
    </div>
  );
}

// ---------------- Connectors (Emergent-style: search + categories + cards) ----------------
const CATS = ['All', 'Login', 'AI', 'Payments', 'Communications', 'Social', 'Developer', 'Storage', 'Other'];
const CANON_STATUS = { CONNECTED: 'connected', NOT_CONNECTED: 'not_connected', PLATFORM_DISABLED: 'not_available' };

// Static, TRUTHFUL catalog for non-social categories (status resolved live where possible).
const STATIC_CONNECTORS = [
  { id: 'google-login', name: 'Google', category: 'Login', desc: 'Let users sign in with Google (OAuth).', status: 'requires_credential' },
  { id: 'email-login', name: 'Email & Password', category: 'Login', desc: 'Built-in JWT email/password auth.', status: 'connected' },
  { id: 'github-login', name: 'GitHub', category: 'Login', desc: 'Sign in / connect a GitHub account.', status: 'requires_credential' },
  { id: 'object-storage', name: 'Object Storage', category: 'Storage', desc: 'Managed file & media storage for uploads.', status: 'connected' },
  { id: 'email-comms', name: 'Email (SMTP/Resend)', category: 'Communications', desc: 'Transactional email delivery.', status: 'requires_credential' },
  { id: 'whatsapp', name: 'WhatsApp', category: 'Communications', desc: 'Send messages via WhatsApp Business.', status: 'requires_credential' },
  { id: 'stripe', name: 'Stripe', category: 'Payments', desc: 'Cards & subscriptions.', status: 'not_configured' },
  { id: 'razorpay', name: 'Razorpay', category: 'Payments', desc: 'Payments for India.', status: 'not_configured' },
];

const STATUS_META = {
  connected: { text: 'Connected', tone: luxe.success },
  requires_credential: { text: 'Requires external credential', tone: luxe.warning },
  not_configured: { text: 'Not configured', tone: luxe.muted },
  not_available: { text: 'Not available', tone: luxe.muted },
};

export function ConnectorsTab() {
  const [social, setSocial] = useState([]);
  const [platform, setPlatform] = useState([]);
  const [ai, setAi] = useState([]);
  const [conns, setConns] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const load = async () => {
    setLoading(true);
    try {
      const [cat, p, c, prov] = await Promise.all([
        api('/connectors/catalog').catch(() => ({ connectors: [] })),
        api('/social/providers').catch(() => ({ providers: [] })),
        api('/studio-marketing/social/connections').catch(() => ({ items: [] })),
        api('/admin/agents/llm/providers').catch(() => ({ providers: [] })),
      ]);
      setPlatform(cat.connectors || []);
      setSocial(p.providers || []);
      const map = {}; (c.items || []).forEach((x) => { map[x.platform] = x; }); setConns(map);
      setAi((prov.providers || []).map((x) => ({
        id: `ai-${x.id}`, name: x.name || x.id, category: 'AI',
        desc: `${(x.capabilities || []).join(', ') || 'LLM'} provider`,
        status: x.status === 'configured' ? 'connected' : 'not_configured',
      })));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const socialCards = social.map((prov) => {
    const conn = conns[prov.id];
    const connected = conn && conn.status === 'connected';
    return {
      id: prov.id, name: prov.label, category: 'Social', desc: (prov.capabilities || []).join(' · ') || prov.connect_kind,
      status: !prov.configured ? 'requires_credential' : connected ? 'connected' : 'not_connected',
      _oauth: prov.connect_kind === 'oauth', _configured: prov.configured, _connected: connected,
    };
  });

  const connectSocial = async (card) => {
    if (!card._configured) return toast.error('This provider requires an external credential to be configured first.');
    if (!card._oauth) { toast.message('Telegram connects from the Marketing page (guided).'); return; }
    try { const r = await api(`/social/oauth/${card.id}/start-ticket`, { method: 'POST' }); if (r.start_url) window.open(r.start_url, '_blank', 'noopener'); }
    catch (e) { toast.error(e.message); }
  };
  const disconnectSocial = async (card) => {
    if (!window.confirm(`Disconnect ${card.name}?`)) return;
    try { await api(`/studio-marketing/social/${card.id}`, { method: 'DELETE' }); toast.success('Disconnected'); load(); } catch (e) { toast.error(e.message); }
  };

  const socialMeta = {};
  social.forEach((prov) => {
    const conn = conns[prov.id];
    socialMeta[prov.id] = { _oauth: prov.connect_kind === 'oauth', _configured: prov.configured, _connected: conn && conn.status === 'connected' };
  });
  const canonCards = platform.map((c) => ({
    id: c.connector_id, name: c.provider_name, category: c.category,
    desc: c.description, faq: c.faq, capabilities: c.capabilities,
    status: CANON_STATUS[c.status] || 'not_configured',
    ...(socialMeta[c.connector_id] || {}),
  }));
  const all = canonCards.length ? canonCards : [...STATIC_CONNECTORS, ...ai, ...socialCards];
  const filtered = all.filter((c) => (cat === 'All' || c.category === cat) && (!q || c.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="manage-connectors">
      <p className="text-sm mb-3" style={{ color: luxe.muted }}>Connect popular services to unlock more features for your project.</p>
      <div className="flex items-center gap-2 mb-3 p-2 rounded-xl" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
        <Search size={15} style={{ color: luxe.muted }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search connectors…" data-testid="connector-search"
          className="flex-1 bg-transparent outline-none text-sm" style={{ color: luxe.text }} />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`connector-cat-${c}`}
            className="px-2.5 py-1 rounded-full text-xs font-bold"
            style={cat === c ? { background: ACCENT, color: '#fff' } : { background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>{c}</button>
        ))}
      </div>
      {loading ? <div className="flex gap-2" style={{ color: luxe.muted }}><Spinner /> Loading…</div>
        : filtered.length === 0 ? <Empty icon={Link2} title="No connectors" desc="Try a different search or category." />
          : (
            <div className="grid sm:grid-cols-2 gap-3">
              {filtered.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META.not_configured;
                const canConnect = c.category === 'Social' && c._configured && !c._connected;
                return (
                  <div key={c.id} data-testid={`connector-card-${c.id}`} className="p-3 rounded-xl flex flex-col gap-2" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold" style={{ color: luxe.text }}>{c.name}</div>
                        <div className="text-[11px]" style={{ color: luxe.muted }}>{c.desc}</div>
                      </div>
                      <Badge text={c.status === 'not_connected' ? 'Not connected' : meta.text} tone={c.status === 'not_connected' ? luxe.muted : meta.tone} />
                    </div>
                    <div className="mt-auto">
                      {c.category === 'Social' && c._connected
                        ? <button onClick={() => disconnectSocial(c)} data-testid={`connector-disconnect-${c.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}><Unplug size={13} /> Disconnect</button>
                        : canConnect
                          ? <button onClick={() => connectSocial(c)} data-testid={`connector-connect-${c.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }}><Link2 size={13} /> Connect</button>
                          : c.status === 'connected'
                            ? <span className="text-xs flex items-center gap-1" style={{ color: luxe.success }}><CheckCircle2 size={13} /> Ready</span>
                            : <span className="text-xs" style={{ color: luxe.muted }}>{meta.text}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      <div className="text-[11px] mt-3 flex items-center gap-1" style={{ color: luxe.muted }}>
        <ShieldCheck size={12} /> Platform credentials stay server-side. Clients authorize their own account via OAuth — tokens are encrypted and never displayed.
      </div>
    </div>
  );
}

// ---------------- AI Providers ----------------
export function ProvidersTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    Promise.all([api('/admin/agents/llm/providers'), api('/admin/agents/llm/policy'), api('/admin/agents/llm/events?limit=20')])
      .then(([p, pol, ev]) => setData({ p, pol, ev }))
      .catch((e) => toast.error(e.message));
  }, []);
  if (!data) return <div className="flex gap-2" style={{ color: luxe.muted }}><Spinner /> Loading…</div>;
  const { p, pol, ev } = data;
  return (
    <div data-testid="manage-providers">
      <div className="flex gap-3 mb-3 text-xs flex-wrap">
        <span style={{ color: luxe.text }}>Providers: {p.count}</span>
        <span style={{ color: luxe.success }}>Configured: {p.configured}</span>
        <span style={{ color: luxe.muted }}>Fallback: {(pol.fallback_order || []).join(' → ') || 'none'}</span>
      </div>
      {(p.providers || []).map((prov) => (
        <Row key={prov.id} testid={`provider-row-${prov.id}`}>
          <div className="min-w-0">
            <div className="font-bold" style={{ color: luxe.text }}>{prov.name || prov.id}</div>
            <div className="text-[11px]" style={{ color: luxe.muted }}>{Object.values(prov.tiers || {}).filter((v, i, a) => a.indexOf(v) === i).slice(0, 4).join(', ') || '—'}</div>
          </div>
          <Badge text={prov.status === 'configured' ? 'Configured' : 'Not configured'} tone={prov.status === 'configured' ? luxe.success : luxe.muted} />
        </Row>
      ))}
      <div className="text-[11px] mt-2 mb-1 uppercase tracking-widest" style={{ color: luxe.muted }}>Recent routing decisions</div>
      {(ev.events || []).length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>No routing events yet.</div>
        : <div className="space-y-1">{(ev.events || []).slice(0, 12).map((e, i) => (
          <div key={i} className="text-[11px] font-mono p-2 rounded-lg" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>
            {(e.at || '').slice(0, 19)} · {e.agent_id || e.agent || '—'} · {e.provider || '—'}/{e.model || '—'} {e.status ? `· ${e.status}` : ''}
          </div>
        ))}</div>}
      <div className="text-[11px] mt-2 flex items-center gap-1" style={{ color: luxe.muted }}><ShieldCheck size={12} /> API keys are never displayed. Runtime policy edits require studio_admin.</div>
    </div>
  );
}

// ---------------- Payments (project-scoped client gateways) ----------------
export function PaymentsTab({ project }) {
  const [catalog, setCatalog] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [connectFor, setConnectFor] = useState(null);   // provider meta
  const [creds, setCreds] = useState({});
  const [mode, setMode] = useState('test');
  const [faqOpen, setFaqOpen] = useState(null);

  const load = async () => {
    try {
      const c = await api('/payments/catalog'); setCatalog(c.providers || []);
      if (project) {
        const s = await api(`/payments/status?project=${encodeURIComponent(project)}`);
        const m = {}; (s.providers || []).forEach((p) => { m[p.id] = p; }); setStatusMap(m);
      }
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, [project]); // eslint-disable-line

  const doConnect = async () => {
    if (!project) return toast.error('Open a project first');
    if (Object.values(creds).every((v) => !v)) return toast.error('Enter your gateway credentials');
    try {
      await api('/payments/connect', { method: 'POST', body: { project, provider: connectFor.id, mode, credentials: creds } });
      toast.success(`${connectFor.name} connected for this project (encrypted)`);
      setConnectFor(null); setCreds({}); load();
    } catch (e) { toast.error(e.message); }
  };
  const doDisconnect = async (p) => {
    if (!window.confirm(`Disconnect ${p.name}?`)) return;
    try { await api('/payments/disconnect', { method: 'POST', body: { project, provider: p.id, credentials: {} } }); toast.success('Disconnected'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="manage-payments">
      <div className="p-3 rounded-xl mb-4 text-xs" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.muted }}>
        <b style={{ color: luxe.text }}>Client payment gateways</b> — connect your OWN merchant account so your customers can pay you.
        This is separate from your Myra subscription/credits. {project ? `Project: ${project}` : 'Open a project to connect a gateway.'}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {catalog.map((p) => {
          const st = statusMap[p.id];
          const connected = st?.status === 'connected';
          return (
            <div key={p.id} data-testid={`payment-card-${p.id}`} className="p-3 rounded-xl" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div><div className="font-bold" style={{ color: luxe.text }}>{p.name}</div><div className="text-[11px]" style={{ color: luxe.muted }}>{p.description}</div></div>
                <Badge text={connected ? 'Connected' : (project ? 'Not configured' : 'Requires credential')} tone={connected ? luxe.success : luxe.muted} />
              </div>
              <div className="flex flex-wrap gap-1 my-2">{(p.features || []).map((f) => <span key={f} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: luxe.surface, color: luxe.muted, border: `1px solid ${luxe.border}` }}>{f}</span>)}</div>
              <div className="flex items-center gap-2">
                {connected
                  ? <button onClick={() => doDisconnect(p)} data-testid={`payment-disconnect-${p.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}>Disconnect</button>
                  : <button onClick={() => { setConnectFor(p); setCreds({}); setMode('test'); }} data-testid={`payment-connect-${p.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-bold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.text }}>Connect</button>}
                {p.docs && <a href={p.docs} target="_blank" rel="noreferrer" className="text-[11px]" style={{ color: luxe.blue }}>Docs</a>}
                <button onClick={() => setFaqOpen(faqOpen === p.id ? null : p.id)} data-testid={`payment-faq-${p.id}`} className="text-[11px] ml-auto" style={{ color: luxe.muted }}>FAQs</button>
              </div>
              {faqOpen === p.id && (
                <ul className="mt-2 space-y-1" data-testid={`payment-faqs-${p.id}`}>
                  {(p.faqs || []).map((q, i) => <li key={i} className="text-[11px]" style={{ color: luxe.textMid }}>• {q}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[11px] mt-3 flex items-center gap-1" style={{ color: luxe.muted }}>
        <ShieldCheck size={12} /> Gateway credentials are encrypted in the Secret Store and never displayed. Payment funds stay separate from AI credits.
      </div>

      {connectFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setConnectFor(null)} data-testid="payment-connect-modal">
          <GlassCard className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-lg mb-1" style={{ color: luxe.text }}>Connect {connectFor.name}</div>
            <div className="text-xs mb-3" style={{ color: luxe.muted }}>Enter your merchant credentials — stored encrypted, never shown again.</div>
            <div className="flex gap-2 mb-3">
              {['test', 'live'].map((mm) => <button key={mm} onClick={() => setMode(mm)} data-testid={`payment-mode-${mm}`} className="px-3 py-1 rounded-full text-xs font-bold" style={mode === mm ? { background: ACCENT, color: '#fff' } : { background: luxe.surface2, color: luxe.muted, border: `1px solid ${luxe.border}` }}>{mm}</button>)}
            </div>
            {(connectFor.credential_fields || ['api_key', 'api_secret']).map((f) => (
              <Input key={f} type="password" placeholder={f} data-testid={`payment-cred-${f}`} value={creds[f] || ''} onChange={(e) => setCreds({ ...creds, [f]: e.target.value })} style={{ marginBottom: 8 }} />
            ))}
            <div className="flex gap-2 mt-2">
              <LuxeButton full onClick={doConnect} data-testid="payment-connect-submit">Connect</LuxeButton>
              <LuxeButton variant="ghost" onClick={() => setConnectFor(null)}>Cancel</LuxeButton>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}

// ---------------- Analytics ----------------
export function AnalyticsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      const [pr, tasks] = await Promise.all([
        api('/admin/agents/projects').catch(() => []),
        api('/admin/agents/tasks?limit=100').catch(() => []),
      ]);
      const projects = (Array.isArray(pr) ? pr : pr.items || []).filter((p) => p.id !== 'cambri');
      const builds = Array.isArray(tasks) ? tasks : [];
      setStats({
        projects: projects.length,
        builds: builds.length,
        completed: builds.filter((t) => t.status === 'completed').length,
        running: builds.filter((t) => ['running', 'awaiting_approval'].includes(t.status)).length,
      });
    })();
  }, []);
  if (!stats) return <Spinner />;
  const cards = [
    ['Projects', stats.projects],
    ['Total builds', stats.builds],
    ['Completed', stats.completed],
    ['In progress', stats.running],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3" data-testid="analytics-cards">
        {cards.map(([label, val]) => (
          <div key={label} className="p-4 rounded-xl" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
            <div className="text-3xl font-black" style={{ color: luxe.text }}>{val}</div>
            <div className="text-xs mt-1" style={{ color: luxe.muted }}>{label}</div>
          </div>
        ))}
      </div>
      <div className="text-xs mt-3" style={{ color: luxe.muted }}>Live counts from your projects and builds.</div>
    </div>
  );
}

// ---------------- Shared tab wrapper ----------------
const TABS = [
  ['connectors', 'Connectors', Link2],
  ['payments', 'Payments', CreditCard],
  ['secrets', 'Secrets', KeyRound],
  ['analytics', 'Analytics', BarChart3],
  ['providers', 'AI Providers', ShieldCheck],
];

export function ManageTabs({ initial = 'connectors', project }) {
  const [tab, setTab] = useState(initial);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`manage-tab-${id}`}
            className="px-3.5 py-2 rounded-full text-sm font-bold luxe-btn"
            style={tab === id ? { background: ACCENT, color: '#fff' } : { background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
            <Icon size={14} className="inline mr-1" />{label}
          </button>
        ))}
      </div>
      {tab === 'secrets' && <SecretsTab />}
      {tab === 'connectors' && <ConnectorsTab />}
      {tab === 'providers' && <ProvidersTab />}
      {tab === 'analytics' && <AnalyticsTab />}
      {tab === 'payments' && <PaymentsTab project={project} />}
    </div>
  );
}
